/**
 * API Client pour la gestion des fournisseurs
 */
import { safeFetch, safeJsonParse, getAuthHeaders } from './api';
import type { Supplier } from '../types/models';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";


export interface SuppliersResponse {
  suppliers: Supplier[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

/**
 * API Suppliers
 */
export const suppliersAPI = {
  /**
   * Récupérer tous les fournisseurs avec filtres optionnels
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    includeInactive?: boolean;
  }): Promise<SuppliersResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.includeInactive) queryParams.append('includeInactive', 'true');

    const url = `${API_URL}/catalog/suppliers${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await safeFetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des fournisseurs : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);

    // Format standard backend V2: {success: true, data: [...], meta: {...}}
    if (apiResponse.success && apiResponse.data) {
      return {
        suppliers: Array.isArray(apiResponse.data) ? apiResponse.data : [],
        pagination: {
          total: apiResponse.meta?.total || 0,
          page: apiResponse.meta?.currentPage || 1,
          limit: apiResponse.meta?.limit || 20,
          pages: apiResponse.meta?.totalPages || 1,
        },
      };
    }

    // Fallback: Si la réponse est directement un tableau
    if (Array.isArray(apiResponse)) {
      return {
        suppliers: apiResponse,
        pagination: {
          total: apiResponse.length,
          page: 1,
          limit: apiResponse.length,
          pages: 1,
        },
      };
    }

    // Par défaut retourner un tableau vide
    return {
      suppliers: [],
      pagination: { total: 0, page: 1, limit: 0, pages: 0 },
    };
  },

  /**
   * Récupérer un fournisseur par ID
   */
  async getById(supplierId: string): Promise<Supplier> {
    const response = await safeFetch(`${API_URL}/catalog/suppliers/${supplierId}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération du fournisseur : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * Créer un nouveau fournisseur
   */
  async create(supplierData: Partial<Supplier>): Promise<Supplier> {
    const response = await safeFetch(`${API_URL}/catalog/suppliers`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(supplierData),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || `Erreur lors de la création du fournisseur : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * Mettre à jour un fournisseur
   */
  async update(supplierId: string, supplierData: Partial<Supplier>): Promise<Supplier> {
    const response = await safeFetch(`${API_URL}/catalog/suppliers/${supplierId}`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(supplierData),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || `Erreur lors de la mise à jour du fournisseur : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * Supprimer un fournisseur (soft delete)
   */
  async delete(supplierId: string): Promise<{ success: boolean }> {
    const response = await safeFetch(`${API_URL}/catalog/suppliers/${supplierId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || `Erreur lors de la suppression du fournisseur : ${response.statusText}`);
    }

    return safeJsonParse(response);
  },

  /**
   * Désactiver un fournisseur
   */
  async deactivate(supplierId: string): Promise<Supplier> {
    const response = await safeFetch(`${API_URL}/catalog/suppliers/${supplierId}/deactivate`, {
      method: "PATCH",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la désactivation du fournisseur : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * Activer un fournisseur
   */
  async activate(supplierId: string): Promise<Supplier> {
    const response = await safeFetch(`${API_URL}/catalog/suppliers/${supplierId}/activate`, {
      method: "PATCH",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de l'activation du fournisseur : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * Récupérer les statistiques d'un fournisseur
   */
  async getStats(supplierId: string): Promise<{
    totalExpenses: number;
    totalAmount: number;
    totalPurchaseOrders: number;
  }> {
    const response = await safeFetch(`${API_URL}/catalog/suppliers/${supplierId}/stats`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des statistiques : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },
};
